import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TextBoxControl from "../common/TextBoxControl";
import CheckBoxControl from "../common/CheckBoxControl";
import ReactTable from 'react-table'
import 'react-table/react-table.css'
import {IsNullOrUndefined} from "../../constants";

// Fields the "search all" box looks through - the ones a PM would have in hand when
// hunting for an item (number, name, or one of the tag-like categories).
const searchFields = ['number', 'name', 'productCategory', 'componentCategory', 'sellCategory',
  'supplyCategory'];

// Only searched while the supplier columns are on screen, so a hit always has a visible cause.
const supplierSearchFields = ['primarySupplierName', 'supplierItemNumber'];

// Null safe contains - most of these columns come straight from optional MYOB custom fields
// and are frequently empty.
const contains = (filter, row) => {
  let value = row[filter.id];
  return IsNullOrUndefined(value)
    ? false
    : value.toString().toLowerCase().indexOf(filter.value.toLowerCase()) >= 0;
};

const yesNoFilter = ({filter, onChange}) => (
  <select onChange={e => onChange(e.target.value)} style={{width: '100%'}} value={filter ? filter.value : 'all'}>
    <option value="all">All</option>
    <option value="true">Yes</option>
    <option value="false">No</option>
  </select>
);

const yesNoFilterMethod = (filter, row) => filter.value === 'all'
  ? true
  : row[filter.id] === (filter.value === 'true');

const money = value => IsNullOrUndefined(value) ? '' : `$${Number(value).toFixed(2)}`;

/**
 * The master inventory grid, shared by the standalone lookup page and the spares order page.
 * Pass onAdd to get a leading + column that hands the row back to the caller.
 */
class MasterInventoryGrid extends Component {
  constructor(props, context) {
    super(props, context);

    this.state = {search: '', showSupplierColumns: false, showWeightColumn: false};

    this.getColumns = this.getColumns.bind(this);
    this.getFilteredItems = this.getFilteredItems.bind(this);
    this.supplierColumnsVisible = this.supplierColumnsVisible.bind(this);
    this.weightColumnVisible = this.weightColumnVisible.bind(this);
  }

  // Must be a real boolean. react-table reads `show` as getFirstDefined(show, true), so an
  // undefined counts as visible - and its body cells then render anyway, because the hide class
  // it applies to a td ('hidden') is not the one react-table.css styles ('-hidden'). That
  // combination shows the data with no matching header, which knocks the grid out of line.
  supplierColumnsVisible() {
    return !!(this.props.allowSupplierColumns && this.state.showSupplierColumns);
  }

  weightColumnVisible() {
    return !!(this.state.showWeightColumn);
  }
 
  getFilteredItems() {
    let {items} = this.props;
    let {search} = this.state;

    if (!search) {
      return items;
    }

    let fields = this.supplierColumnsVisible() ? searchFields.concat(supplierSearchFields) : searchFields;
    let term = search.toLowerCase();
    return items.filter(x => fields.some(f =>
      !IsNullOrUndefined(x[f]) && x[f].toString().toLowerCase().indexOf(term) >= 0));
  }

  getColumns() {
    let {onAdd, showSellItem} = this.props;
    let showSupplierColumns = this.supplierColumnsVisible();
    let showWeight = this.weightColumnVisible();
    // Real boolean - see the note on supplierColumnsVisible.
    let showWholesale = !!this.props.showWholesale;
    let showBaseSellingPrice = !!this.props.showBaseSellingPrice;

    let columns = [
      {
        Header: 'Image',
        accessor: 'imageName',
        maxWidth: 55,
        sortable: false,
        Filter: () => <span></span>,
        Cell: c => IsNullOrUndefined(c.original.imageName) || c.original.imageName === ''
          ? <span></span>
          : <img src={`/img/powder_coating_sheet/${c.original.imageName}`}
                 alt={c.original.number}
                 style={{width: '35px'}}
                 onError={e => { e.target.style.display = 'none'; }} />,
      },
      {
        Header: 'Item Number',
        accessor: 'number',
        maxWidth: 120,
        filterMethod: contains,
      },
      {
        Header: 'Item Name',
        accessor: 'name',
        filterMethod: contains,
      },
      {
        Header: 'Product Category',
        accessor: 'productCategory',
        filterMethod: contains,
      },
      {
        Header: 'Component Category',
        accessor: 'componentCategory',
        filterMethod: contains,
      },
      {
        Header: 'Sell Cat.',
        accessor: 'sellCategory',
        maxWidth: 120,
        filterMethod: contains,
      },
      {
        Header: 'Supply Cat.',
        accessor: 'supplyCategory',
        maxWidth: 100,
        filterMethod: contains,
      },
      {
        Header: 'Weight (kg)',
        accessor: 'weight',
        maxWidth: 110,
        filterMethod: contains,
        show: showWeight,
      },
      {
        Header: 'Primary Supplier',
        accessor: 'primarySupplierName',
        filterMethod: contains,
        show: showSupplierColumns,
        
      },
      {
        Header: 'Supplier Item #',
        accessor: 'supplierItemNumber',
        maxWidth: 140,
        filterMethod: contains,
        show: showSupplierColumns,
      },
    ];

    // Pointless when the caller has already filtered to sell items.
    // if (showSellItem) {
    //   columns.push({
    //     Header: 'Sell Item',
    //     accessor: 'isSold',
    //     maxWidth: 100,
    //     Cell: c => <span><i className={`fa ${c.original.isSold ? 'fa-check' : 'fa-times'}`}/></span>,
    //     Filter: yesNoFilter,
    //     filterMethod: yesNoFilterMethod,
    //   });
    // }

    // Prices last. Ordering a spare is a wholesale conversation, so that view drops retail
    // entirely - the same way the product group grid hides its retail column.
    if (showBaseSellingPrice) {
      columns = columns.concat([{
        Header: 'Retail',
        accessor: 'baseSellingPrice',
        maxWidth: 120,
        Cell: c => <span>{money(c.original.baseSellingPrice)}</span>,
        Filter: () => <span></span>,
      }]);
    }

    if (showWholesale) {
      columns = columns.concat([{
        Header: 'Wholesale',
        accessor: 'wholesalePrice',
        maxWidth: 120,
        Cell: c => <span>{money(c.original.wholesalePrice)}</span>,
        Filter: () => <span></span>,
      }]);
    }
    
    
    if (onAdd) {
      columns.unshift({
        id: 'actions',
        Header: '',
        maxWidth: 60,
        sortable: false,
        Filter: () => <span></span>,
        Cell: c => (
          <button onClick={(e) => { e.preventDefault(); onAdd(c.original); }}>
            <i className="fa fa-plus"/>
          </button>
        ),
      });
    }

    return columns;
  }

  render() {
    let {loading, allowSupplierColumns} = this.props;
    let {search, showSupplierColumns, showWeightColumn} = this.state;
    let items = this.getFilteredItems();

    return (
      <div>
        <div className={'pi-row'}>
          <div className={'pi-col-md-12'}>
            <TextBoxControl
              labelText="Search"
              name="search"
              value={search}
              isDebounce={true}
              debounceeTimeOut={300}
              icon={'search'}
              onChange={(e) => this.setState({search: e.target.value})}
            />
          </div>
        </div>
        <div className={'pi-row'}>
          <div className={'pi-col-md-4'}>
            <CheckBoxControl
                labelText="&nbsp;Show weight"
                name="showWeightColumn"
                value={showWeightColumn}
                onChange={(e) => this.setState({showWeightColumn: e.target.checked})}
            />            
          </div>
        </div>
        {allowSupplierColumns &&
            <div className={'pi-row'}>
              <div className={'pi-col-md-4'}>
                <CheckBoxControl
                    labelText="&nbsp;Show supplier details"
                    name="showSupplierColumns"
                    value={showSupplierColumns}
                    onChange={(e) => this.setState({showSupplierColumns: e.target.checked})}
                />
              </div>
            </div>}

        {loading
          ? <p>Loading inventory...</p>
          : <ReactTable
              sortable={true}
              filterable={true}
              showPagination={true}
              data={items}
              columns={this.getColumns()}
              defaultPageSize={25}
              minRows={10}
            />}
      </div>
    );
  }
}

MasterInventoryGrid.propTypes = {
  items: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  showSellItem: PropTypes.bool,
  // Offers the "Show supplier details" toggle. Off by default, and the columns stay hidden
  // until it is ticked - the spares order page never opts in.
  allowSupplierColumns: PropTypes.bool,
  // Show the organisation's wholesale price instead of the two retail columns.
  showWholesale: PropTypes.bool,
  showBaseSellingPrice: PropTypes.bool,
  onAdd: PropTypes.func,
};

export default MasterInventoryGrid;
